'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  Tabs,
  Tab,
  Button,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import ThemeProvider from '../../theme/ThemeProvider';
import { useDAppState } from '../../context/DAppStateProvider';
import {
  CATEGORIES,
  translateErrorMessage,
  getChainInfoByName,
  ItemFilter,
  isEVMChain,
} from '../../utils';
import { txToast } from '../../component/TxToast';
import NFTCard from '../../component/NFTCard';
import { DEFAULT_DAPP_STATE } from '@cai/shared/dapp';
import { LoadingOverlay } from '../../component/LoadingOverlay';

const ALL_SHOW = 'All';
const FAVORITE_SHOW = 'Favorite';
const CATEGORY_KEY = 'category';

function readCategoryMemory() {
  try {
    const raw = localStorage.getItem(CATEGORY_KEY);
    if (raw == null || raw === '') return {};
    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch {
    return {};
  }
}

const NftCollectionPage = () => {
  const { chain } = useParams();
  const chainInfo = getChainInfoByName(chain);

  const navigate = useNavigate();

  const [selectedCollection, setSelectedCollection] = useState(
    () => readCategoryMemory()[chain] ?? ALL_SHOW,
  );

  useEffect(() => {
    const collectionMemory = readCategoryMemory();
    localStorage.setItem(
      CATEGORY_KEY,
      JSON.stringify({ ...collectionMemory, [chain]: selectedCollection }),
    );
  }, [chain, selectedCollection]);

  const { isInitingStatus, state, walletConnection, buyNFTs, favoriteNFTs, toggleFavoriteNFT } =
    useDAppState();

  const collectionState = state[chain] ?? DEFAULT_DAPP_STATE[chain];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpeningWallet, setIsOpeningWallet] = useState(false);

  const handleCollectionChange = (event, newValue) => {
    setSelectedCollection(newValue);
  };

  const filteredNfts =
    selectedCollection === ALL_SHOW
      ? collectionState.listedNfts
      : selectedCollection == FAVORITE_SHOW
        ? collectionState.listedNfts.filter((nft) => favoriteNFTs.includes(nft.displayKey))
        : collectionState.listedNfts.filter((nft) =>
            (Array.isArray(nft.categories) ? nft.categories : []).includes(selectedCollection),
          );

  const buyFromEscrow = async (nft) => {
    setIsOpeningWallet(true);
    buyNFTs([nft])
      .then((txLink) => {
        txToast(`Bought NFT successfully!!!`, txLink, ItemFilter.MyWallet);
      })
      .catch((e) => {
        toast.error(translateErrorMessage(e));
      })
      .finally(() => {
        setIsOpeningWallet(false);
      });
  };

  const handleBackClick = () => {
    navigate('/marketplace');
  };

  return (
    <ThemeProvider>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Back to Networks
          </Button>
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: 'rgba(19, 19, 26, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `0 0 20px ${chainInfo.glowColor}`,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Avatar
            src={chainInfo.image}
            alt={chainInfo.name}
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'transparent',
              p: 1,
              boxShadow: `0 0 15px ${chainInfo.glowColor}`,
            }}
          />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{ color: chainInfo.color }}
            >
              {chainInfo.name} ({chainInfo.symbol}) NFTs
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore and collect unique NFTs on the {chainInfo.name} network
            </Typography>
          </Box>
        </Paper>
        <div style={{ position: 'relative' }}>
          {(collectionState?.isLoadingGlobalState ||
            collectionState?.isLoadingListedNfts ||
            isOpeningWallet ||
            isInitingStatus(chain)) && <LoadingOverlay />}
          <Paper
            sx={{
              width: '100%',
              mb: 4,
              borderRadius: 2,
              background: 'rgba(19, 19, 26, 0.7)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={selectedCollection}
              onChange={handleCollectionChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile={true}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: chainInfo.color,
                  height: 3,
                  borderRadius: 1.5,
                },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    color: chainInfo.color,
                  },
                },
              }}
            >
              {[ALL_SHOW, ...CATEGORIES, FAVORITE_SHOW].map((type) => (
                <Tab key={type} label={type} value={type} />
              ))}
            </Tabs>
          </Paper>
          {filteredNfts.length > 0 ? (
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                pr: 1,
                height: `calc(100vh - ${isMobile ? 510 : 500}px)`,
              }}
            >
              <Grid container spacing={3}>
                {filteredNfts.map((nft) => (
                  <Grid key={nft.displayKey} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <NFTCard
                      chain={chain}
                      currency={collectionState.currency ?? collectionState.symbol}

                      nft={nft}
                      isEnabled={true}

                      isListedByYou={nft.owner == walletConnection.address}
                      buyNFT={() => buyFromEscrow(nft)}

                      isFavorite={favoriteNFTs.includes(nft.displayKey)}
                      onToggleFavorite={() => toggleFavoriteNFT(nft.displayKey)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {selectedCollection === 'Favorite'
                  ? "You haven't favorited any NFTs yet"
                  : 'No NFTs found in this collection'}
              </Typography>
            </Box>
          )}
        </div>
      </Container>
    </ThemeProvider>
  );
};

export default NftCollectionPage;
